import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { RichTextField } from '@sitecore-jss/sitecore-jss-nextjs';
import SiteNameRichText from 'components/SiteNameRichText/SiteNameRichText';
import ButtonPrimary from 'components/shared/Button/PrimaryButton';
import NextLink from 'next/link';
import { withLocationParam } from 'lib/utils/locationSearchParam';
import { showLoader } from 'src/features/loaderSlice';
import { formatMarkerTooltip } from './locationSearchMarkerTooltip';
import {
  StMapInfoAddress,
  StMapInfoButtonWrap,
  StMapInfoCloseButton,
  StMapInfoDivider,
  StMapInfoHeader,
  StMapInfoMetaRow,
  StMapInfoTitle,
  StMapInfoWindow,
} from './mapCoveoResultToCard.styles';
import { SiteCard } from './mapCoveoResultToCard.types';

type LocationSearchMapInfoWindowProps = {
  site: SiteCard;
  viewSiteLabel: string;
  markerTooltipText?: string;
  hasLocationContext?: boolean;
  searchLocality?: string;
  siteNameFormat?: RichTextField;
  onClose?: () => void;
};

function LocationSearchMapInfoWindow({
  site,
  viewSiteLabel,
  markerTooltipText,
  hasLocationContext = false,
  searchLocality,
  siteNameFormat,
  onClose,
}: LocationSearchMapInfoWindowProps): JSX.Element {
  const dispatch = useDispatch();
  const siteHref = withLocationParam(site.url, searchLocality);
  const locationLine = [site.city, site.state].filter(Boolean).join(', ');
  const hasDistance = hasLocationContext && typeof site.distanceMiles === 'number';
  const enrollmentTemplate = markerTooltipText
    ?.replace(/\s*·\s*\{distance\}/g, '')
    ?.replace(/\{distance\}/g, '');
  const enrollmentText = formatMarkerTooltip(
    { ...site, distanceMiles: undefined },
    enrollmentTemplate
  );
  const metaParts = [enrollmentText];
  if (hasDistance) {
    metaParts.push(`${site.distanceMiles!.toFixed(1)} mi`);
  }
  const metaLine = metaParts.filter(Boolean).join(' · ');

  const siteInfoForName = {
    siteName: site.name,
    siteCity: site.city ?? '',
    siteState: site.state ?? '',
    latitude: site.latitude ?? 0,
    longitude: site.longitude ?? 0,
    isHybridSite: false,
  };

  return (
    <StMapInfoWindow>
      <StMapInfoHeader>
        {siteNameFormat?.value ? (
          <StMapInfoTitle>
            <SiteNameRichText siteNameFormat={siteNameFormat} siteInfo={siteInfoForName} />
          </StMapInfoTitle>
        ) : (
          <StMapInfoTitle>{site.name}</StMapInfoTitle>
        )}
        {onClose && (
          <StMapInfoCloseButton aria-label="Close site details" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </StMapInfoCloseButton>
        )}
      </StMapInfoHeader>

      <StMapInfoDivider />

      {site.address && <StMapInfoAddress>{site.address}</StMapInfoAddress>}
      {locationLine && <StMapInfoAddress>{locationLine}</StMapInfoAddress>}

      {metaLine && <StMapInfoMetaRow>{metaLine}</StMapInfoMetaRow>}

      <StMapInfoButtonWrap>
        <ButtonPrimary
          href={siteHref}
          component={NextLink}
          onClick={() => {
            dispatch(showLoader());
          }}
        >
          {viewSiteLabel}
        </ButtonPrimary>
      </StMapInfoButtonWrap>
    </StMapInfoWindow>
  );
}

export default LocationSearchMapInfoWindow;
